import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ComponentType } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Download,
  ExternalLink,
  X,
  Eye,
  FileText,
  Loader2,
  Plus,
  Send,
  Video,
  Trash2,
} from 'lucide-react';
import {
  useAtendimento,
  useAtendimentos,
  useAtributo,
  useExamesImagem,
} from '@/hooks/useAtendimentos';
import {
  useClinicalEncounter,
  useCreateProblem,
  useFinishClinicalEncounter,
  useInactivateProblem,
  useActiveProblemCatalog,
  usePatientClinicalEncounters,
  useProblems,
  useUpdateSOAP,
} from '@/hooks/useConsultasClinicas';
import { usePaciente } from '@/hooks/usePaciente';
import { useAgendaMedico } from '@/hooks/useAgendaMedico';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ClassificationList } from '@/components/atendimento/ClassificationList';
import { apiUrl } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';
import { usePatientContextStore } from '@/stores/patient-context-store';
import { useTelemedicinaStore } from '@/stores/telemedicina-store';
import {
  base64ToBlob,
  classificarAtributo,
  classificarProcedimento,
  downloadBlob,
  formatarData,
  sanitizeFilename,
} from '@/lib/utils';
import type { ClinusAtendimento, ClinusAtributo, ExameImagem } from '@/types/api';

export const Route = createFileRoute('/pep-vida/_authed/consultas/$id')({
  component: ClinicalEncounterPage,
});

interface SoapDraft {
  anamnese: string;
  subjetivo: string;
  objetivo: string;
  avaliacao: string;
  plano: string;
}

const emptySoap: SoapDraft = {
  anamnese: '',
  subjetivo: '',
  objetivo: '',
  avaliacao: '',
  plano: '',
};

function ClinicalEncounterPage() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: encounter, isLoading, error, refetch } = useClinicalEncounter(id);
  const { data: paciente } = usePaciente(encounter?.cdPaciente ?? null);
  const { data: atendimentoAtual } = useAtendimento(encounter?.cdAtendimento ?? null);
  const { data: agendaDoPaciente } = useAgendaMedico(formatarDataIso(encounter?.startedAt ?? ''), encounter?.cdPaciente);
  const { data: problems } = useProblems(encounter?.cdPaciente ?? null);
  const updateSOAP = useUpdateSOAP(id);
  const finishEncounter = useFinishClinicalEncounter(id);
  const createProblem = useCreateProblem(encounter?.cdPaciente ?? 0);
  const inactivateProblem = useInactivateProblem(encounter?.cdPaciente ?? 0);
  const { data: problemCatalog } = useActiveProblemCatalog();
  const setPatient = usePatientContextStore((state) => state.setPatient);
  const clearPatient = usePatientContextStore((state) => state.clearPatient);

  const [draft, setDraft] = useState<SoapDraft>(emptySoap);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const draftVersionRef = useRef(0);
  const draftRef = useRef<SoapDraft>(emptySoap);
  const dirtyRef = useRef(false);
  const lastUnloadFlushVersionRef = useRef(-1);
  const [problemCatalogId, setProblemCatalogId] = useState('');
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyEncounterId, setHistoryEncounterId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'soap' | 'prescricao' | 'conduta'>('soap');
  const { data: openedHistory, isLoading: historyLoading } = useClinicalEncounter(historyEncounterId);
  const isHistoryOpen = historyPanelOpen;
  const displayedEncounter = openedHistory ?? encounter;
  const agendaItem = agendaDoPaciente?.items?.find((item) => item.cdAtendimento === displayedEncounter?.cdAtendimento);
  const procedimentoCabecalho = agendaItem?.procedimento ?? atendimentoAtual?.dsProcedimento ?? 'Consulta';
  const convenioCabecalho = agendaItem?.plano ?? atendimentoAtual?.dsModalidade ?? 'Não informado';
  const telemedicinaAtiva = procedimentoCabecalho.toLocaleUpperCase('pt-BR').includes('CONSULTA TELEMEDICINA');

  const isFinished = encounter?.status === 'finalizado';
  const endedAtMs = encounter?.endedAt ? new Date(encounter.endedAt).getTime() : 0;
  const editWindowOpen = isFinished && endedAtMs > 0 && Date.now() - endedAtMs < 24 * 60 * 60 * 1000;
  const editWindowExpiresAt = editWindowOpen ? new Date(endedAtMs + 24 * 60 * 60 * 1000) : null;
  const isReadOnly = isHistoryOpen || (isFinished && !editWindowOpen);

  const saveSOAP = async (automatic = false) => {
    const versionAtStart = draftVersionRef.current;
    if (automatic) setAutoSaveState('saving');
    await updateSOAP.mutateAsync({
      anamnese: draft.anamnese,
      subjetivo: draft.subjetivo,
      objetivo: draft.objetivo,
      avaliacao: draft.avaliacao,
      plano: draft.plano,
    });
    if (versionAtStart === draftVersionRef.current) {
      setHasUnsavedChanges(false);
      setAutoSaveState('saved');
    }
  };

  const changeDraft = (field: keyof SoapDraft, value: string) => {
    draftVersionRef.current += 1;
    setHasUnsavedChanges(true);
    setAutoSaveState('idle');
    setDraft((current) => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    if (!hasUnsavedChanges || isReadOnly || !encounter) return;
    const timer = window.setTimeout(() => {
      void saveSOAP(true).catch(() => setAutoSaveState('idle'));
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [draft, hasUnsavedChanges, isReadOnly, encounter?.id]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const flushDraftBeforeUnload = () => {
      if (
        !dirtyRef.current ||
        isReadOnly ||
        !encounter ||
        lastUnloadFlushVersionRef.current === draftVersionRef.current
      ) {
        return;
      }

      lastUnloadFlushVersionRef.current = draftVersionRef.current;
      void fetch(`${apiUrl}/api/consultas-clinicas/${encounter.id}/soap`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(draftRef.current),
        keepalive: true,
      });
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // O aviso continua protegendo o médico, mas o rascunho também é enviado
      // antes do reload/fechamento. Isso evita perda de texto no Ctrl+F5.
      flushDraftBeforeUnload();
      event.preventDefault();
      event.returnValue = '';
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushDraftBeforeUnload();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasUnsavedChanges, isReadOnly, encounter?.id]);

  useEffect(() => {
    draftRef.current = draft;
    dirtyRef.current = hasUnsavedChanges;
  }, [draft, hasUnsavedChanges]);

  useEffect(() => {
    if (!displayedEncounter) return;
    setDraft({
      anamnese: displayedEncounter.soapNote?.anamnese ?? '',
      subjetivo: displayedEncounter.soapNote?.subjetivo ?? '',
      objetivo: displayedEncounter.soapNote?.objetivo ?? '',
      avaliacao: displayedEncounter.soapNote?.avaliacao ?? '',
      plano: displayedEncounter.soapNote?.plano ?? '',
    });
    setHasUnsavedChanges(false);
    setAutoSaveState('idle');
  }, [displayedEncounter?.id, displayedEncounter?.soapNote?.updatedAt]);

  useEffect(() => {
    if (!encounter || !paciente) return;
    sessionStorage.setItem(`pep-vida:consulta-ativa:${encounter.cdPaciente}`, encounter.id);
    setPatient({
      cdPaciente: encounter.cdPaciente,
      cdAtendimento: encounter.cdAtendimento,
      clinicalEncounterId: encounter.id,
      nome: paciente.ds_paciente,
      status: encounter.status === 'finalizado' ? 'Finalizada' : 'Em atendimento',
      meta: `${paciente.cd_paciente}`,
    });
  }, [encounter?.id, encounter?.status, paciente?.cd_paciente, paciente?.ds_paciente, setPatient]);

  if (isLoading) return <LoadingState />;
  if (error) {
    if ((error as Error).message.includes('não encontrada')) throw notFound();
    return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  }
  if (!encounter) throw notFound();
  if (!paciente) return <LoadingState />;

  const handleLeave = () => {
    if (hasUnsavedChanges && !window.confirm('O prontuário ainda não foi salvo. Tem certeza que quer fechar assim mesmo?')) return;
    void navigate({ to: '/pep-vida/agenda' });
  };

  const finalizeEncounter = async () => {
    if (hasUnsavedChanges) await saveSOAP();
    await finishEncounter.mutateAsync();
    clearPatient();
  };

  const addProblem = async () => {
    const title = problemCatalog?.find((item) => item.id === problemCatalogId)?.name ?? '';
    if (!title || !encounter.cdPaciente || !problemCatalogId) return;
    await createProblem.mutateAsync({ title, catalogId: problemCatalogId, status: 'ativo' });
    setProblemCatalogId('');
  };

  return (
    <div className="-m-3 min-h-full bg-[#f7f5fb] px-3 py-4 text-[#29233d] md:-m-6 md:px-10 md:py-6" data-testid="consulta-clinica">
      <div className="mx-auto max-w-[1230px] space-y-4">
      <div className="flex flex-col gap-3 border-b border-[#e5e0f0] bg-white/90 px-1 pb-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between md:px-0">
        <Button variant="ghost" size="sm" onClick={handleLeave}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para a agenda
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant={isHistoryOpen ? 'outline' : isFinished ? 'secondary' : 'default'}>
            {isHistoryOpen ? 'Histórico aberto' : isFinished ? 'Finalizada' : 'Em atendimento'}
          </Badge>
          {editWindowOpen && editWindowExpiresAt && !isHistoryOpen && (
            <span className="hidden text-xs text-muted-foreground lg:inline">
              Edição disponível até {editWindowExpiresAt.toLocaleString('pt-BR')}
            </span>
          )}
          <span className="hidden text-xs text-muted-foreground sm:inline">
            {autoSaveState === 'saving' ? 'Salvando automaticamente…' : autoSaveState === 'saved' ? 'Salvo automaticamente' : hasUnsavedChanges ? 'Alterações pendentes' : ''}
          </span>
          {!isFinished && (
            <Button
              onClick={() => void finalizeEncounter()}
              disabled={isReadOnly || finishEncounter.isPending}
            >
              {finishEncounter.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Finalizar
            </Button>
          )}
        </div>
      </div>

      {paciente && (
        <Card className={isHistoryOpen ? 'rounded-xl border-2 border-amber-400 bg-amber-50/30 shadow-[0_10px_28px_rgba(74,57,125,0.07)]' : 'rounded-xl border-[#e5e0f0] bg-white shadow-[0_10px_28px_rgba(74,57,125,0.07)]'}>
          <CardContent className="flex items-center justify-between gap-4 p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
                {paciente.ds_paciente.split(' ').slice(0, 2).map((part) => part[0]).join('')}
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-primary">Paciente em atendimento</div>
                <h2 className="mt-1 text-xl font-semibold">{paciente.ds_paciente}</h2>
                <div className="mt-2 text-sm text-muted-foreground">
                  {calcularIdade(paciente.dt_nascimento)} · {paciente.dt_nascimento} · CPF {paciente.nr_cpf ?? '-'}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Sexo: {formatarSexo(paciente.ds_sexo)} · Convênio: {convenioCabecalho} · Procedimento: {procedimentoCabecalho}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant={isHistoryOpen ? 'outline' : 'consulta'} className={isHistoryOpen ? 'border-amber-500 text-amber-800' : ''}>
                    {isHistoryOpen ? 'Histórico aberto' : 'Atendimento atual'}: {formatarData(displayedEncounter?.startedAt ?? encounter.startedAt)}
                  </Badge>
                  {isHistoryOpen && historyLoading && <span className="text-xs text-amber-800">Carregando consulta...</span>}
                </div>
              </div>
            </div>
            <Badge variant={isFinished ? 'secondary' : 'consulta'}>{isFinished ? 'Finalizada' : 'Em atendimento'}</Badge>
          </CardContent>
        </Card>
      )}

      <ProblemsCard
        problems={problems}
        isFinished={isReadOnly}
        addProblem={addProblem}
        createProblem={createProblem}
        inactivateProblem={inactivateProblem}
        problemCatalog={problemCatalog}
        problemCatalogId={problemCatalogId}
        setProblemCatalogId={setProblemCatalogId}
      />

      <HistoricoTimeline
        cdPaciente={encounter.cdPaciente}
        cdAtendimentoAtual={encounter.cdAtendimento}
        historyOpen={isHistoryOpen}
        selectedHistoryId={historyEncounterId}
        onSelectHistory={(selectedId) => { setHistoryPanelOpen(true); setHistoryEncounterId(selectedId); }}
        onCloseHistory={() => { setHistoryPanelOpen(false); setHistoryEncounterId(null); }}
      />

      <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_265px]">
        <Card className={isHistoryOpen ? 'min-h-[620px] rounded-xl border-2 border-amber-400 bg-white shadow-[0_10px_28px_rgba(74,57,125,0.07)]' : 'min-h-[620px] rounded-xl border-[#e5e0f0] bg-white shadow-[0_10px_28px_rgba(74,57,125,0.07)]'}>
          <CardHeader className="border-b pb-0">
            <div className="flex flex-wrap gap-5">
              <TabButton active={activeTab === 'soap'} onClick={() => setActiveTab('soap')}>
                Evolução SOAP
              </TabButton>
              <TabButton active={activeTab === 'prescricao'} onClick={() => setActiveTab('prescricao')}>
                Prescrição
              </TabButton>
              <TabButton active={activeTab === 'conduta'} onClick={() => setActiveTab('conduta')}>
                Conduta
              </TabButton>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 p-5">
            {activeTab === 'soap' && (
              <>
                <SoapField label="Anamnese" placeholder="Anamnese clínica" value={draft.anamnese} disabled={isReadOnly} onChange={(value) => changeDraft('anamnese', value)} large />
                <div className="grid gap-4 lg:grid-cols-2">
                  <SoapField label="Subjetivo" placeholder="Queixa principal, historia e sintomas" value={draft.subjetivo} disabled={isReadOnly} onChange={(value) => changeDraft('subjetivo', value)} />
                  <SoapField label="Objetivo" placeholder="Exame fisico, sinais vitais e achados" value={draft.objetivo} disabled={isReadOnly} onChange={(value) => changeDraft('objetivo', value)} />
                  <SoapField label="Avaliação" placeholder="Hipoteses diagnosticas e analise clinica" value={draft.avaliacao} disabled={isReadOnly} onChange={(value) => changeDraft('avaliacao', value)} />
                  <SoapField label="Plano" placeholder="Condutas, orientacoes e seguimento" value={draft.plano} disabled={isReadOnly} onChange={(value) => changeDraft('plano', value)} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <ClassificationList
                    type="ciap2"
                    label="CIAP-2"
                    encounterId={displayedEncounter?.id ?? id}
                    disabled={isReadOnly}
                    placeholder="Buscar código ou descrição (ex: diabetes, A01)"
                  />
                  <ClassificationList
                    type="cid10"
                    label="CID-10"
                    encounterId={displayedEncounter?.id ?? id}
                    disabled={isReadOnly}
                    placeholder="Buscar código ou descrição (ex: refluxo, E11)"
                  />
                </div>
              </>
            )}
            {activeTab === 'prescricao' && <PrescriptionTab />}
            {activeTab === 'conduta' && (
              <SoapField label="Orientações ao paciente" placeholder="Orientações, sinais de alarme e retorno" value={draft.plano} disabled={isReadOnly} onChange={(value) => changeDraft('plano', value)} large />
            )}
            {(updateSOAP.error || finishEncounter.error) && <p className="text-sm text-destructive">{((updateSOAP.error || finishEncounter.error) as Error).message}</p>}
          </CardContent>
        </Card>

        <div className="space-y-5">
          <ResumoClinicoPanel
            cdPaciente={encounter.cdPaciente}
            cdAtendimentoAtual={encounter.cdAtendimento}
            encounterId={displayedEncounter?.id ?? encounter.id}
            patientName={paciente?.ds_paciente ?? 'Paciente'}
            patientCpf={paciente?.nr_cpf ?? ''}
            patientBirth={paciente?.dt_nascimento ?? ''}
            procedure={atendimentoAtual?.dsProcedimento ?? 'Consulta'}
            doctorName={atendimentoAtual?.dsMedico ?? ''}
            doctorCrm={(atendimentoAtual as { dsCrm?: string | null } | undefined)?.dsCrm ?? ''}
            doctorCrmUf={(atendimentoAtual as { dsCrmUf?: string | null } | undefined)?.dsCrmUf ?? ''}
            cid={encounter.soapNote?.cid ?? ''}
            ciap2={encounter.soapNote?.ciap2 ?? ''}
            historyOpen={isHistoryOpen}
            readOnly={isReadOnly}
            telemedicinaAtiva={telemedicinaAtiva}
          />

        </div>
      </div>

    </div>
    </div>
  );
}

function formatarDataHora(dt?: string | null): string {
  if (!dt) return '?';
  const data = new Date(dt);
  if (Number.isNaN(data.getTime())) return formatarData(dt);
  return `${data.toLocaleDateString('pt-BR')} ${data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function HistoricoTimeline({
  cdPaciente,
  cdAtendimentoAtual,
  historyOpen,
  selectedHistoryId,
  onSelectHistory,
  onCloseHistory,
}: {
  cdPaciente: number;
  cdAtendimentoAtual: number;
  historyOpen: boolean;
  selectedHistoryId: string | null;
  onSelectHistory: (encounterId: string) => void;
  onCloseHistory: () => void;
}) {
  const { data: encounters, isLoading, error } = usePatientClinicalEncounters(cdPaciente);
  const { data: clinusAtendimentos } = useAtendimentos(cdPaciente, { refetchInterval: false });
  const history = (encounters ?? [])
    .filter((item) => item.cdAtendimento !== cdAtendimentoAtual && item.status === 'finalizado')
    .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());
  const procedimentoPorAtendimento = new Map(
    (clinusAtendimentos ?? []).map((item) => [item.cdAtendimento, item.dsProcedimento]),
  );
  const medicoPorAtendimento = new Map(
    (clinusAtendimentos ?? []).map((item) => [item.cdAtendimento, item.dsMedico]),
  );

  return (
    <Card className={historyOpen ? 'border-2 border-amber-400 bg-amber-50/30' : 'border-[#e5e0f0] bg-white shadow-[0_10px_28px_rgba(74,57,125,0.07)]'}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Linha do tempo PEP</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">Histórico de consultas realizadas no PEP.</p>
          </div>
          {historyOpen && <Button type="button" variant="outline" size="sm" onClick={onCloseHistory}>Voltar ao atendimento atual</Button>}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <MiniLoading label="Carregando histórico..." />}
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        {!isLoading && !error && history.length === 0 && <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">Nenhum histórico PEP anterior encontrado.</p>}
        {history.length > 0 && (
          <div className="relative overflow-x-auto pb-2 pt-2">
            <div className="absolute left-5 right-5 top-7 h-0.5 bg-[#d8cfee]" />
            <div className="relative flex min-w-max gap-4">
              {history.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelectHistory(item.id)}
                  className={`relative w-56 rounded-lg border bg-background p-2.5 pt-6 text-left transition hover:border-primary hover:shadow-sm ${selectedHistoryId === item.id ? 'border-2 border-amber-400 bg-amber-50' : 'border-[#e5e0f0]'}`}
                >
                  <span className={`absolute left-3 top-3 h-2.5 w-2.5 rounded-full ring-4 ring-background ${selectedHistoryId === item.id ? 'bg-amber-500' : 'bg-primary'}`} />
                  <div className="truncate text-sm font-semibold">
                    {procedimentoPorAtendimento.get(item.cdAtendimento) || 'Consulta'}
                  </div>
                  <div className="mt-1 truncate text-xs text-muted-foreground">
                    {medicoPorAtendimento.get(item.cdAtendimento) || 'Executante não informado'}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{formatarDataHora(item.startedAt)}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProblemsCard({
  problems,
  isFinished,
  addProblem,
  createProblem,
  inactivateProblem,
  problemCatalog,
  problemCatalogId,
  setProblemCatalogId,
}: {
  problems?: Array<{ id: string; title: string; status: string; ciap2?: string | null; cid?: string | null }>;
  isFinished: boolean;
  addProblem: () => void;
  createProblem: { isPending: boolean; error: unknown };
  inactivateProblem: { isPending: boolean; error: unknown; mutate: (problemId: string) => void };
  problemCatalog?: Array<{ id: string; name: string; code?: string | null; category?: string | null }>;
  problemCatalogId: string;
  setProblemCatalogId: (value: string) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="h-4 w-4" />
          Problemas do paciente
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {problems && problems.length > 0 ? problems.map((problem) => (
            <div key={problem.id} className="flex items-center gap-2 rounded-full border bg-background py-1 pl-3 pr-1">
              <span className="text-sm font-medium">{problem.title}</span>
              <Badge variant="outline" className="capitalize">{problem.status}</Badge>
              {!isFinished && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full text-muted-foreground hover:text-destructive"
                  title="Inativar problema"
                  aria-label={`Inativar ${problem.title}`}
                  onClick={() => inactivateProblem.mutate(problem.id)}
                  disabled={inactivateProblem.isPending}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          )) : <p className="text-sm text-muted-foreground">Nenhum problema cadastrado para este paciente.</p>}
        </div>
        {!isFinished && (
          <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="problem-title">Novo problema</Label>
              <Select id="problem-catalog" value={problemCatalogId} onChange={(e) => setProblemCatalogId(e.target.value)}>
                <option value="">Selecione um problema cadastrado</option>
                {(problemCatalog ?? []).map((item) => <option key={item.id} value={item.id}>{item.name}{item.code ? ` · ${item.code}` : ''}</option>)}
              </Select>
            </div>
            <Button variant="outline" onClick={addProblem} disabled={!problemCatalogId || createProblem.isPending}>
              {createProblem.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Adicionar
            </Button>
          </div>
        )}
        {Boolean(createProblem.error) && <p className="text-sm text-destructive">{(createProblem.error as Error).message}</p>}
        {Boolean(inactivateProblem.error) && <p className="text-sm text-destructive">{(inactivateProblem.error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}

function ResumoClinicoPanel({
  cdPaciente,
  cdAtendimentoAtual,
  encounterId,
  patientName,
  patientCpf,
  patientBirth,
  procedure,
  doctorName,
  doctorCrm,
  doctorCrmUf,
  cid,
  ciap2,
  historyOpen,
  readOnly,
  telemedicinaAtiva,
}: {
  cdPaciente: number;
  cdAtendimentoAtual: number;
  encounterId: string;
  patientName: string;
  patientCpf: string;
  patientBirth: string;
  procedure: string;
  doctorName: string;
  doctorCrm: string;
  doctorCrmUf: string;
  cid: string;
  ciap2: string;
  historyOpen: boolean;
  readOnly: boolean;
  telemedicinaAtiva: boolean;
}) {
  const [documents, setDocuments] = useState<Array<{ id: string; type: string; title: string; content: string }>>([]);
  const [editor, setEditor] = useState<{ id?: string; type: string; title: string; content: string } | null>(null);
  const documentEditorRef = useRef<HTMLDivElement>(null);
  const openTelemedicina = useTelemedicinaStore((state) => state.open);
  useEffect(() => { api.get<Array<{ id: string; type: string; title: string; content: string }>>(`/api/generated-documents/encounter/${encounterId}`).then(setDocuments).catch(() => setDocuments([])); }, [encounterId]);
  useEffect(() => { if (documentEditorRef.current) documentEditorRef.current.innerHTML = editor?.content ?? ''; }, [editor?.id, editor?.title]);
  async function saveDocument() {
    if (!editor || readOnly) return;
    const saved = editor.id
      ? await api.patch<{ id: string; type: string; title: string; content: string }>(`/api/generated-documents/${editor.id}`, editor)
      : await api.post<{ id: string; type: string; title: string; content: string }>(`/api/generated-documents/encounter/${encounterId}`, editor);
    setDocuments((current) => editor.id ? current.map((item) => item.id === saved.id ? saved : item) : [saved, ...current]);
    setEditor(null);
  }
  async function deleteDocument() {
    if (!editor || readOnly || !editor.id) return;
    if (!window.confirm('Deseja excluir este documento?')) return;
    await api.delete('/api/generated-documents/' + editor.id);
    setDocuments((current) => current.filter((item) => item.id !== editor.id));
    setEditor(null);
  }
  function printDocument() { if (!editor) return; const win = window.open(`${window.location.origin}/pep-vida/documento-medico`, '_blank', 'width=900,height=700'); if (!win) return; const logoUrl = 'https://vida.vidavg.com.br/pep-vida/brand/logo-vida.png'; win.document.write(`<html><head><title>${editor.title}</title><style>@page{margin:8mm 10mm 21mm}*{box-sizing:border-box}html,body{margin:0;padding:0}body{font-family:Arial,sans-serif;color:#111;line-height:1.4;padding-bottom:15mm;font-weight:400!important}body *{font-weight:400!important}.content b,.content strong{font-weight:700!important}header{position:relative;min-height:29mm;border-bottom:1px solid #0f766e;padding:0 0 5px;margin:0 0 6mm}header .brand{display:flex;justify-content:space-between;align-items:flex-start;gap:14px}header .identity{padding-top:0}header img{display:block;width:125px;height:auto;max-height:20mm;object-fit:contain}h1{font-size:18px;margin:0 0 6px}h2{font-size:14px;margin:0 0 4px;color:#0f766e}.meta{font-size:10px;color:#555}.content{padding-bottom:0;margin:0;}.content p,.content h1,.content h2,.content h3,.content h4,.content h5,.content h6{margin:0;padding:0;}.content{font-weight:400!important}.content>div{font-weight:400!important}.content font[size="1"]{font-size:10px}.content font[size="2"]{font-size:13px}.content font[size="3"]{font-size:16px}.content font[size="4"]{font-size:18px}.content font[size="5"]{font-size:24px}footer{position:fixed;left:0;right:0;bottom:0;height:13mm;border-top:1px solid #9ca3af;padding-top:3px;text-align:center;font-size:8.5px;line-height:1.25;color:#4b5563;background:#fff}footer strong{display:block;color:#0f766e}@media print{header{break-inside:avoid}footer{position:fixed;left:0;right:0;bottom:0}}</style></head><body><header><div class="brand"><div class="identity"><h1>PEP-VIDA \u00b7 Documento Medico</h1><h2>${patientName}</h2><div class="meta">Paciente: ${cdPaciente} · Atendimento: ${cdAtendimentoAtual} · Emissão: ${new Date().toLocaleDateString('pt-BR')}</div></div><img src="${logoUrl}" alt="Vida Diagnóstico e Saúde"></div></header><div class="content">${editor.content}</div><footer><strong>Vida Centro de Diagnósticos Médico e Saúde</strong>RUA MAMED UNTAR, 749, CENTRO, VÁRZEA GRANDE - MT - CEP: 78110-315<br>Telefone: 65 3029-9700</footer></body></html>`); win.document.close(); try { win.history.replaceState({}, '', '/pep-vida/documento-medico'); } catch {} win.focus(); win.print(); }
  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Ações rápidas</CardTitle>
        </CardHeader>
          <CardContent className="grid grid-cols-1 gap-2">
          {telemedicinaAtiva && (
            <Button
              type="button"
              variant="outline"
              disabled={historyOpen}
              className="h-10 justify-start px-3 text-xs"
            onClick={async () => {
              try {
                const session = await api.post<{ token: string }>(`/api/telemedicina/${encounterId}/session`, {});
                const telemedicinaUrl = new URL('https://vida.vidavg.com.br/pep-vida/telemedicina/');
                telemedicinaUrl.searchParams.set('token', session.token);
                telemedicinaUrl.searchParams.set('role', 'doctor');
                openTelemedicina(telemedicinaUrl.toString());
              } catch {
                window.alert('Não foi possível abrir a sala de telemedicina. Verifique se o atendimento ainda está aberto.');
              }
            }}
            >
              <Video className="mr-2 h-4 w-4" />
              Telemedicina
            </Button>
          )}
          <QuickAction disabled={readOnly || historyOpen} icon={FileText} label="Receita" onGenerate={(x) => setEditor(x)} context={{ patientName, patientCpf, patientBirth, procedure, cid, ciap2, doctorName, doctorCrm, doctorCrmUf }} />
          <QuickAction disabled={readOnly || historyOpen} icon={ClipboardList} label="Solicitar exame" onGenerate={(x) => setEditor(x)} context={{ patientName, patientCpf, patientBirth, procedure, cid, ciap2, doctorName, doctorCrm, doctorCrmUf }} />
          <QuickAction disabled={readOnly || historyOpen} icon={FileText} label="Atestado" onGenerate={(x) => setEditor(x)} context={{ patientName, patientCpf, patientBirth, procedure, cid, ciap2, doctorName, doctorCrm, doctorCrmUf }} />
          <QuickAction disabled={readOnly || historyOpen} icon={Send} label="Encaminhamento" onGenerate={(x) => setEditor(x)} context={{ patientName, patientCpf, patientBirth, procedure, cid, ciap2, doctorName, doctorCrm, doctorCrmUf }} />
          <QuickAction disabled={readOnly || historyOpen} icon={FileText} label="Outros" onGenerate={(x) => setEditor(x)} context={{ patientName, patientCpf, patientBirth, procedure, cid, ciap2, doctorName, doctorCrm, doctorCrmUf }} />
        </CardContent>
      </Card>
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Documentos gerados</CardTitle></CardHeader><CardContent className="space-y-1.5">{documents.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum documento gerado nesta consulta.</p> : documents.map((d) => <button key={d.id} className="w-full rounded-md border px-3 py-2 text-left text-sm hover:border-primary" onClick={() => setEditor(d)}><div className="truncate font-medium">{d.title}</div></button>)}</CardContent></Card>
      {editor && <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"><div className="w-full max-w-2xl rounded-lg bg-background p-5 shadow-xl"><div className="flex justify-between"><h2 className="text-lg font-semibold">{readOnly ? 'Visualizar documento' : 'Editar documento'}</h2><Button variant="ghost" size="icon" onClick={() => setEditor(null)}><X className="h-4 w-4" /></Button></div><div className="mt-4 rounded-md border-l-4 border-primary bg-muted p-4"><div className="font-bold">PEP-VIDA · {editor.title}</div><div className="mt-1 text-sm">Paciente: {patientName}</div><div className="text-xs text-muted-foreground">Código: {cdPaciente} · Atendimento: {cdAtendimentoAtual} · Data: {new Date().toLocaleDateString('pt-BR')}</div></div><Input className="mt-4" value={editor.title} disabled={readOnly} onChange={(e) => setEditor({ ...editor, title: e.target.value })} /><div ref={documentEditorRef} contentEditable={!readOnly} suppressContentEditableWarning onInput={(e) => setEditor({ ...editor, content: e.currentTarget.innerHTML })} className="mt-3 min-h-64 w-full overflow-auto rounded-md border bg-background p-3 text-sm" /><div className="mt-4 flex flex-wrap gap-2">{!readOnly && <Button onClick={saveDocument}>Salvar documento</Button>}<Button variant="outline" onClick={printDocument}>Imprimir / Baixar PDF</Button>{!readOnly && editor.id && <Button variant="destructive" onClick={deleteDocument}><Trash2 className="mr-2 h-4 w-4" />Excluir documento</Button>}</div></div></div>}
    </>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      className={`border-b-2 pb-3 text-sm transition-colors ${
        active
          ? 'border-primary font-semibold text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function PrescriptionTab() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold">Prescrição</h3>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar medicamento
        </Button>
      </div>
      <div className="rounded-lg border bg-background p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold">Losartana 50 mg</div>
            <div className="mt-1 text-sm text-muted-foreground">Tomar 1 comprimido pela manhã, uso contínuo</div>
          </div>
          <Button variant="outline">Editar</Button>
        </div>
      </div>
    </div>
  );
}

function QuickAction({
  disabled = false,
  icon: Icon,
  label,
  onGenerate,
  context,
}: {
  disabled?: boolean;
  icon: ComponentType<{ className?: string }>;
  label: string;
  onGenerate: (document: { type: string; title: string; content: string }) => void;
  context: { patientName: string; patientCpf: string; patientBirth: string; procedure: string; cid: string; ciap2: string; doctorName: string; doctorCrm: string; doctorCrmUf: string };
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Array<{ id: string; name: string; content: string; version: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [doctorProfile, setDoctorProfile] = useState({ nome: '', crm: '', crmUf: '' });
  const doctorCdMedico = useAuthStore((state) => state.user?.cd_medico);
  const type = label === 'Receita' ? 'receita' : label === 'Solicitar exame' ? 'pedido' : label === 'Atestado' ? 'atestado' : label === 'Outros' ? 'outro' : 'encaminhamento';
  async function openModels() {
    setOpen(true); setLoading(true);
    try {
      const requests = [api.get(`/api/admin/document-templates/active?type=${type}`)];
      if (doctorCdMedico) requests.push(api.get(`/api/admin/document-templates/doctor-profile/${doctorCdMedico}`));
      const [models, profile] = await Promise.all(requests);
      setItems(models as Array<{ id: string; name: string; content: string; version: number }>);
      if (profile) setDoctorProfile(profile as { nome: string; crm: string; crmUf: string });
    } catch { setItems([]); } finally { setLoading(false); }
  }
  function fill(content: string) {
    const birth = context.patientBirth || '';
    const age = calcularIdade(birth);
    const now = new Date();
    const date = now.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    const shortDate = now.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return content.replace(/\{\{paciente\.nome\}\}/g, context.patientName).replace(/\{\{paciente\.cpf\}\}/g, context.patientCpf || 'Não informado').replace(/\{\{paciente\.data_nascimento\}\}/g, birth || 'Não informado').replace(/\{\{paciente\.idade\}\}/g, age).replace(/\{\{procedimento\}\}/g, context.procedure || 'Não informado').replace(/\{\{medico\.nome\}\}/g, doctorProfile.nome || context.doctorName || 'Não informado').replace(/\{\{medico\.crm\}\}/g, doctorProfile.crm || context.doctorCrm || 'Não informado').replace(/\{\{medico\.crm_uf\}\}/g, doctorProfile.crmUf || context.doctorCrmUf || 'Não informado').replace(/\{\{data\}\}/g, shortDate).replace(/\{\{data\.extenso\}\}/g, date).replace(/\{\{cid\}\}/g, context.cid || 'Não informado').replace(/\{\{ciap2\}\}/g, context.ciap2 || 'Não informado');
  }
  return (
    <>
    <button
      type="button"
      onClick={openModels}
      disabled={disabled}
      aria-disabled={disabled}
      title={disabled ? 'Indisponível no modo histórico' : undefined}
      className={`flex h-10 min-w-0 items-center gap-2 rounded-md border px-3 text-left text-xs font-semibold transition-colors ${disabled ? 'cursor-not-allowed bg-muted text-muted-foreground opacity-60' : 'bg-background hover:border-primary hover:bg-primary/5'}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />
      <span className="min-w-0 break-words">{label}</span>
    </button>
    {open && <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setOpen(false)}><div className="w-full max-w-lg rounded-lg bg-background p-5 shadow-xl" onClick={(e) => e.stopPropagation()}><div className="flex items-center justify-between"><h2 className="text-lg font-semibold">Modelos de {label}</h2><Button variant="ghost" size="icon" onClick={() => setOpen(false)}><X className="h-4 w-4" /></Button></div>{loading ? <div className="py-8 text-center text-sm text-muted-foreground">Carregando modelos...</div> : items.length ? <div className="mt-4 space-y-2">{items.map((item) => <button key={item.id} type="button" className="w-full rounded-md border p-3 text-left hover:border-primary hover:bg-primary/5" onClick={() => { onGenerate({ type, title: item.name, content: fill(item.content) }); setOpen(false); }}><div className="font-medium">{item.name}</div><div className="mt-1 line-clamp-2 text-xs text-muted-foreground">Versão {item.version} · {item.content}</div></button>)}</div> : <div className="mt-4 rounded-md bg-muted p-4 text-sm text-muted-foreground">Nenhum modelo ativo cadastrado.</div>}</div></div>}
    </>
  );
}

function HistoricoClinusPanel({
  cdPaciente,
  cdAtendimentoAtual,
}: {
  cdPaciente: number;
  cdAtendimentoAtual: number;
}) {
  const { data: atendimentos, isLoading, error } = useAtendimentos(cdPaciente, {
    refetchInterval: false,
  });
  const historico = (atendimentos ?? []).filter(
    (item) => item.cdAtendimento !== cdAtendimentoAtual && item.cdAtendimento > 0,
  );
  const consultasHistorico = historico.filter((item) => isConsultaClinica(item));
  const [selectedAtendimento, setSelectedAtendimento] = useState<number | null>(null);
  const [selectedAtributo, setSelectedAtributo] = useState<ClinusAtributo | null>(null);

  useEffect(() => {
    if (consultasHistorico.length === 0) {
      setSelectedAtendimento(null);
      return;
    }
    const selectedStillExists = consultasHistorico.some(
      (item) => item.cdAtendimento === selectedAtendimento,
    );
    if (!selectedAtendimento || !selectedStillExists) {
      setSelectedAtendimento(consultasHistorico[0].cdAtendimento);
    }
  }, [consultasHistorico, selectedAtendimento]);

  useEffect(() => {
    setSelectedAtributo(null);
  }, [selectedAtendimento]);

  const { data: detalhe, isFetching: loadingDetalhe } = useAtendimento(selectedAtendimento);
  const atributoQuery = useAtributo(
    selectedAtendimento,
    selectedAtributo?.cd_valor ?? null,
  );
  const atributoDetalhe = atributoQuery.data;
  const loadingAtributo = atributoQuery.isFetching;
  const {
    data: examesImagem,
    isLoading: loadingExamesImagem,
    error: examesImagemError,
  } = useExamesImagem(cdPaciente);
  const [selectedExame, setSelectedExame] = useState<ExameImagem | null>(null);
  const [laudoFullscreen, setLaudoFullscreen] = useState<ExameImagem | null>(null);
  const atributos = (detalhe?.atributos ?? []).filter(
    (atributo) =>
      atributo.cd_valor > 0 &&
      (!atributo.dt_data || !detalhe?.dtData || sameClinusDate(atributo.dt_data, detalhe.dtData)),
  );

  useEffect(() => {
    if (selectedAtributo || atributos.length === 0) return;
    setSelectedAtributo(atributos[0]);
  }, [atributos, selectedAtributo]);


  useEffect(() => {
    if (!examesImagem || examesImagem.length === 0) {
      setSelectedExame(null);
      return;
    }
    const selectedStillExists = examesImagem.some(
      (exame) => exame.cdExame === selectedExame?.cdExame,
    );
    if (!selectedExame || !selectedStillExists) {
      setSelectedExame(examesImagem[0]);
    }
  }, [examesImagem, selectedExame]);


  useEffect(() => {
    if (!laudoFullscreen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLaudoFullscreen(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [laudoFullscreen]);

  function openPacsJavaViewer(exame: ExameImagem) {
    if (!exame.nrControle) {
      alert('Este exame não possui número de controle para abrir as imagens no PACS.');
      return;
    }

    const url =
      exame.pacsUrl ||
      `http://clinux.vidavg.com.br:9090/pacs/viewer.jnlp?accessionNumber=${encodeURIComponent(String(exame.nrControle))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="space-y-5">
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" />
          Histórico Clinux
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && <MiniLoading label="Carregando histórico..." />}
        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
        {!isLoading && consultasHistorico.length === 0 && (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Nenhuma consulta anterior encontrada no Clinux.
          </p>
        )}

        {consultasHistorico.length > 0 && (
          <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
            {consultasHistorico.slice(0, 20).map((atendimento) => (
              <HistoricoItem
                key={atendimento.cdAtendimento}
                atendimento={atendimento}
                active={selectedAtendimento === atendimento.cdAtendimento}
                onSelect={() => setSelectedAtendimento(atendimento.cdAtendimento)}
              />
            ))}
          </div>
        )}

        {selectedAtendimento && (
          <div className="border-t pt-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-sm font-medium">Documentos do atendimento</div>
              <Badge variant="outline">{selectedAtendimento}</Badge>
            </div>
            {loadingDetalhe && <MiniLoading label="Buscando documentos..." />}
            {!loadingDetalhe && atributos.length === 0 && (
              <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                Sem documentos RTF para este atendimento.
              </p>
            )}
            {!loadingDetalhe && atributos.length > 0 && (
              <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                {atributos.map((atributo) => (
                  <AtributoRow
                    key={atributo.cd_valor}
                    atributo={atributo}
                    active={selectedAtributo?.cd_valor === atributo.cd_valor}
                    onView={() => setSelectedAtributo(atributo)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {selectedAtributo && (
          <div className="border-t pt-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{selectedAtributo.ds_atributo}</div>
                <div className="text-xs text-muted-foreground">
                  Prévia do documento
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={!atributoDetalhe?.bb_valor}
                onClick={() => {
                  if (!atributoDetalhe?.bb_valor || !selectedAtendimento) return;
                  const blob = base64ToBlob(atributoDetalhe.bb_valor, 'application/rtf');
                  downloadBlob(
                    blob,
                    `atendimento_${selectedAtendimento}_${sanitizeFilename(
                      selectedAtributo.ds_atributo,
                    )}.rtf`,
                  );
                }}
                aria-label="Baixar RTF"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
            {loadingAtributo && <MiniLoading label="Abrindo RTF..." />}
            {!loadingAtributo && atributoQuery.error && (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                {(atributoQuery.error as Error).message}
              </p>
            )}
            {!loadingAtributo && atributoDetalhe?.bb_valor && selectedAtendimento && (
              <iframe
                title={`Prévia ${selectedAtributo.ds_atributo}`}
                src={`${apiUrl}/api/atendimentos/${selectedAtendimento}/atributos/${selectedAtributo.cd_valor}/preview.pdf`}
                className="h-[520px] w-full rounded-md border bg-muted/20"
              />
            )}
            {!loadingAtributo && atributoDetalhe && !atributoDetalhe.bb_valor && (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                O Clinux retornou o documento sem conteúdo RTF para prévia.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="h-4 w-4" />
          Exames de imagem e laudos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadingExamesImagem && <MiniLoading label="Carregando exames..." />}
        {examesImagemError && (
          <p className="text-sm text-destructive">{(examesImagemError as Error).message}</p>
        )}
        {!loadingExamesImagem && (examesImagem ?? []).length === 0 && (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Nenhum exame de imagem encontrado no Clinux.
          </p>
        )}
        {(examesImagem ?? []).length > 0 && (
          <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
            {(examesImagem ?? []).slice(0, 30).map((exame) => (
              <ExameImagemItem
                key={exame.cdExame}
                exame={exame}
                active={selectedExame?.cdExame === exame.cdExame}
                onSelect={() => setSelectedExame(exame)}
              />
            ))}
          </div>
        )}

        {selectedExame && (
          <div className="border-t pt-4">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {selectedExame.procedimento || 'Exame de imagem'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {selectedExame.data || 'Sem data'}
                  {selectedExame.nrControle ? ` · Controle ${selectedExame.nrControle}` : ''}
                </div>
              </div>
              {selectedExame.temImagem && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => openPacsJavaViewer(selectedExame)}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Imagem
                </Button>
              )}
            </div>

            {selectedExame.temLaudo ? (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-center"
                onClick={() => setLaudoFullscreen(selectedExame)}
              >
                <Eye className="mr-2 h-4 w-4" />
                Visualizar laudo
              </Button>
            ) : (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                Este exame ainda não possui laudo disponível para prévia.
              </p>
            )}

            {selectedExame.temImagem && !selectedExame.nrControle && (
              <p className="mt-3 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                O Clinux indica imagem disponível, mas não retornou número de controle para abrir o PACS.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    {laudoFullscreen && (
      <div className="fixed inset-0 z-50 flex flex-col bg-background">
        <div className="flex h-14 shrink-0 items-center justify-between border-b bg-background px-4">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">
              {laudoFullscreen.procedimento || 'Laudo do exame'}
            </div>
            <div className="text-xs text-muted-foreground">
              {laudoFullscreen.data || 'Sem data'}
              {laudoFullscreen.nrControle ? ` · Controle ${laudoFullscreen.nrControle}` : ''}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLaudoFullscreen(null)}
            aria-label="Fechar visualização do laudo"
          >
            <X className="mr-2 h-4 w-4" />
            Fechar
          </Button>
        </div>
        <iframe
          title={`Laudo ${laudoFullscreen.cdExame}`}
          src={`${apiUrl}/api/exames-imagem/${laudoFullscreen.cdExame}/laudo.pdf`}
          className="min-h-0 flex-1 border-0 bg-muted/20"
        />
      </div>
    )}
    </div>
  );
}

function calcularIdade(value: string): string {
  const parts = value.split('/').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return 'Não informado';
  const birth = new Date(parts[2], parts[1] - 1, parts[0]);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const beforeBirthday = now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 0 ? `${age} anos` : 'Não informado';
}

function formatarDataIso(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatarSexo(value?: string): string {
  if (value === 'M') return 'Masculino';
  if (value === 'F') return 'Feminino';
  if (value === 'O') return 'Outro';
  return 'Não informado';
}


void HistoricoClinusPanel;

function isConsultaClinica(atendimento: ClinusAtendimento) {
  const procedimento = (atendimento.dsProcedimento || '').toUpperCase();
  return (
    classificarProcedimento(atendimento.dsProcedimento) === 'consulta' ||
    procedimento.includes('RETORNO')
  );
}

function HistoricoItem({
  atendimento,
  active,
  onSelect,
}: {
  atendimento: ClinusAtendimento;
  active: boolean;
  onSelect: () => void;
}) {
  const tipo = classificarProcedimento(atendimento.dsProcedimento);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-md border p-3 text-left transition-colors ${
        active ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{formatarData(atendimento.dtData)}</div>
          <div className="mt-1 truncate text-sm font-medium">
            {atendimento.dsProcedimento || 'Atendimento'}
          </div>
        </div>
        <Badge variant={tipo} className="shrink-0 capitalize">
          {tipo}
        </Badge>
      </div>
    </button>
  );
}


function ExameImagemItem({
  exame,
  active,
  onSelect,
}: {
  exame: ExameImagem;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-md border p-3 text-left transition-colors ${
        active ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{exame.data || 'Sem data'}</div>
          <div className="mt-1 truncate text-sm font-medium">
            {exame.procedimento || 'Exame de imagem'}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {exame.temLaudo && <Badge variant="exame">Laudo</Badge>}
            {exame.temImagem && <Badge variant="outline">Imagem</Badge>}
            {exame.modalidade && <Badge variant="secondary">{exame.modalidade}</Badge>}
          </div>
        </div>
        {exame.nrControle && (
          <Badge variant="outline" className="shrink-0">
            {exame.nrControle}
          </Badge>
        )}
      </div>
    </button>
  );
}

function AtributoRow({
  atributo,
  active,
  onView,
}: {
  atributo: ClinusAtributo;
  active: boolean;
  onView: () => void;
}) {
  const tipo = classificarAtributo(atributo);
  return (
    <div className={`rounded-md border p-2 ${active ? 'border-primary bg-primary/5' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{atributo.ds_atributo}</div>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant={tipo} className="capitalize">
              {tipo}
            </Badge>
            {(atributo.dt_criacao || atributo.dt_data) && (
              <span>{atributo.dt_criacao || atributo.dt_data}</span>
            )}
          </div>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onView} aria-label="Visualizar RTF">
          <Eye className="mr-2 h-4 w-4" />
          Visualizar
        </Button>
      </div>
    </div>
  );
}

function MiniLoading({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

function sameClinusDate(left: string, right: string) {
  return left.slice(0, 10) === right.slice(0, 10);
}

function SoapField({
  label,
  placeholder,
  value,
  disabled,
  onChange,
  large = false,
}: {
  label: string;
  placeholder?: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  large?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <textarea
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`${large ? 'min-h-40' : 'min-h-36'} w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
      />
    </div>
  );
}
